// src/Pages/StatusPayment/PaymentPending.jsx
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { m } from 'framer-motion';
import { fadeIn } from '../../Functions/GlobalAnimations';
import axios from 'axios';
import config from '../../config';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';

const PaymentPending = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [virementInstructions, setVirementInstructions] = useState('');
  const [error, setError] = useState('');

  const orderId = searchParams.get('orderId');
  const isRtl = i18n.language === 'ar';
  const direction = isRtl ? 'rtl' : 'ltr';
const currency = i18n.language === 'fr' ? 'DT' : 'دينار';

  // Données bancaires (à adapter selon ton vrai RIB)
  const bankDetails = {
    iban: 'TN59 0803 3012 0710 0080 0665',
    bank: 'BIAT',
    accountHolder: 'JAAFAR MOHAMED ALI',
    swiftCode: 'BIATTNTT',
  };

  useEffect(() => {
    const fetchOrder = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      if (!orderId) {
        setError(t('payment_pending.no_order_id') || 'Aucun numéro de commande fourni.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${config.SITE_API_URL}/orderById/${orderId}`, {
          headers: { Authorization: `Bearer ${token}`,
        'x-api-key': config.SITE_API_KEY },
        });

        const fetchedOrder = response.data;

        if (fetchedOrder.paymentMethod !== 'virement' || fetchedOrder.paymentStatus !== 'pending') {
          navigate('/dashboard');
          return;
        }

        setOrder(fetchedOrder);

        const instructions = isRtl
          ? `يرجى إجراء تحويل بنكي بقيمة ${(fetchedOrder.totalPrice || 0).toFixed(3)} ${currency} مع الرقم المرجعي الإلزامي :\n\n` +
            `${fetchedOrder.code_orders}\n\n` +
            `سيتم تفعيل طلبك تلقائياً بعد التحقق من الدفع (خلال 48 ساعة عمل كحد أقصى).\n\n` +
            `شكراً لثقتكم بنا !`
          : `Veuillez effectuer un virement bancaire de ${(fetchedOrder.totalPrice || 0).toFixed(3)} ${currency} avec la référence obligatoire :\n\n` +
            `${fetchedOrder.code_orders}\n\n` +
            `Votre commande sera activée automatiquement après validation du paiement (48h ouvrables max).\n\n` +
            `Merci pour votre confiance !`;

        setVirementInstructions(instructions);
        setLoading(false);
      } catch (err) {
        console.error('Erreur:', err);
        setError(err.response?.data?.message || t('payment_pending.error_fetch') || 'Impossible de charger les détails.');
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate, t, isRtl]);

  const handleCopyReference = () => {
    if (order?.code_orders) {
      navigator.clipboard.writeText(order.code_orders);
      toast.success(isRtl ? 'تم نسخ الرقم المرجعي' : 'Référence copiée !');
    }
  };

  const handleGoToDashboard = () => navigate('/dashboard');

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ direction }}>
        <Spinner animation="border" variant="primary" />
        <p className="ms-3 text-lg">{t('payment_pending.loading') || 'Chargement...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" style={{ direction }}>
        <div className="bg-white p-10 rounded-2xl shadow-xl max-w-lg text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#003479' }}>
            {t('payment_pending.error')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleGoToDashboard}
            className="px-8 py-3 rounded-xl font-semibold text-white"
            style={{ backgroundColor: '#003479' }}
          >
            {t('payment_pending.back_to_dashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden" style={{ direction }}>
      {/* Fond décoratif flou */}
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div
          className="absolute top-20 right-20 w-80 h-80 rounded-full blur-3xl"
          style={{ backgroundColor: '#f29f2f' }}
        />
        <div
          className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl"
          style={{ backgroundColor: '#0879bd' }}
        />
      </div>

      <m.section className="relative z-10 py-12 min-md:py-20" {...fadeIn}>
        <Container>
          <Row className="justify-center">
            <Col lg={9} xl={8}>
              {/* En-tête */}
              <div className="text-center mb-12">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-lg"
                  style={{ backgroundColor: '#e8f5e9' }}
                >
                  <span className="text-xxxl">⏳</span>
                </div>
                <h1 className="text-lg min-md:text-xxlg font-bold mb-4" style={{ color: '#003479' }}>
                  {t('payment_pending.title')}
                </h1>
                <p className="text-lg text-gray-600">
                  {t('payment_pending.order_ref')} {' '}
                  <span className="font-bold" style={{ color: '#0879bd' }}>
                    {order?.code_orders || orderId || '—'}
                  </span>{' '}
                  — <span style={{ color: '#f29f2f' }} className="font-bold">
                    {t('payment_pending.status_pending')}
                  </span>
                </p>
              </div>

              {/* Montant principal */}
              <div
                className="rounded-3xl p-10 min-md:p-12 mb-10 shadow-2xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #fef8e7 0%, #fff9f0 100%)',
                  border: '3px solid #f29f2f',
                }}
              >
                <p className="text-xl text-gray-700 mb-3">
                  {t('payment_pending.amount_to_transfer')}
                </p>
                <h2 className="text-xlg min-md:text-xxxl font-extrabold" style={{ color: '#003479' }}>
                  {(order?.totalPrice || 0).toFixed(3) || '0.000'} {currency}
                </h2>
              </div>

              {/* Instructions + détails */}
              <div className="rounded-3xl shadow-2xl overflow-hidden mb-10 bg-white">
                <div
                  className="px-8 min-md:px-12 py-8 text-center min-md:text-start"
                  style={{ backgroundColor: '#f0f8ff', borderBottom: '3px solid #e5e7eb' }}
                >
                  <h3 className="text-xxl font-bold" style={{ color: '#003479' }}>
                    {t('payment_pending.bank_transfer_instructions')}
                  </h3>
                </div>

               <div className="px-8 min-md:px-12 py-10 min-md:py-12 space-y-10">
                   {/* Référence */}
                  <div>
                    <h4 className="text-xlg font-bold mb-4" style={{ color: '#0879bd' }}>
                      {t('payment_pending.reference_required')}
                    </h4>
                    <div
                      className="p-6 rounded-2xl border-2 flex flex-col sm:flex-row items-center justify-between gap-4"
                      style={{ borderColor: '#f29f2f', backgroundColor: '#fffbf5' }}
                    >
                      <code
                        className="font-mono text-xxlg min-md:text-xl font-bold break-all text-center sm:text-start"
                        style={{ color: '#003479' }}
                      >
                        {order?.code_orders || '—'}
                      </code>
                    <p className="text-sm text-gray-600 text-center sm:text-start">
                      {t('payment_pending.reference_note')}
                    </p>
                    </div>
                  </div>

                  {/* Détails bancaires */}
                  <div>
                    <h4 className="text-xlg font-bold mb-5" style={{ color: '#0879bd' }}>
                      {t('payment_pending.bank_details')}
                    </h4>
                    <div className="grid min-md:grid-cols-2 gap-5">
                      {[
                        { label: t('payment_pending.iban'), value: bankDetails.iban },
                        { label: t('payment_pending.bank_name'), value: bankDetails.bank },
                        { label: t('payment_pending.account_holder'), value: bankDetails.accountHolder },
                        { label: t('payment_pending.swift'), value: bankDetails.swiftCode },
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="p-5 rounded-xl border shadow-sm"
                          style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
                        >
                          <p className="text-sm font-semibold mb-2" style={{ color: '#0879bd' }}>
                            {item.label}
                          </p>
                          <code className="text-base font-mono break-all" style={{ color: '#003479' }}>
                            {item.value}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes importantes */}
                  <div
                    className="p-7 rounded-2xl border-l-4"
                    style={{ borderColor: '#f29f2f', backgroundColor: '#fff9f5' }}
                  >
                    <h4 className="text-xl font-bold mb-4" style={{ color: '#003479' }}>
                      {t('payment_pending.important_info')}
                    </h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <span style={{ color: '#f29f2f', fontSize: '1.4rem' }}>✓</span>
                        <span>{t('payment_pending.note_reference')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span style={{ color: '#f29f2f', fontSize: '1.4rem' }}>✓</span>
                        <span>{t('payment_pending.note_delay')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span style={{ color: '#f29f2f', fontSize: '1.4rem' }}>✓</span>
                        <span>{t('payment_pending.note_activation')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bouton retour */}
              <div className="text-center">
                <button
                  onClick={handleGoToDashboard}
                  className="w-full sm:w-auto px-12 py-4 rounded-2xl font-bold text-lg text-white transition-all duration-300 hover:shadow-2xl hover:scale-105"
                  style={{
                    backgroundColor: '#003479',
                    boxShadow: '0 10px 30px rgba(0, 52, 121, 0.25)',
                  }}
                >
                  {t('payment_pending.back_to_dashboard')}
                </button>
              </div>

            </Col>
          </Row>
        </Container>
      </m.section>

      <ToastContainer position={isRtl ? 'bottom-right' : 'bottom-left'} autoClose={3000} />
    </div>
  );
};

export default PaymentPending;